import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type Segment, type SegmentQuery } from '../api';

type SegmentPreview = { count: number; sample: { name: string; email: string }[] };

type EditorState = {
  id?: number;
  name: string;
  tagsInput: string;
  status: 'subscribed' | 'unsubscribed';
  sourcesInput: string;
  minOpens: string;
};

const EMPTY: EditorState = {
  name: '',
  tagsInput: '',
  status: 'subscribed',
  sourcesInput: '',
  minOpens: '',
};

function editorToQuery(e: EditorState): SegmentQuery {
  const q: SegmentQuery = {};
  const tags = e.tagsInput.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (tags.length) q.tags = { any: tags };
  q.status = e.status;
  const sources = e.sourcesInput.split(',').map((s) => s.trim()).filter(Boolean);
  if (sources.length) q.sources = sources;
  const n = Number(e.minOpens);
  if (e.minOpens.trim() && Number.isFinite(n) && n > 0) q.minOpens = Math.floor(n);
  return q;
}

function queryToEditor(s: Segment): EditorState {
  return {
    id: s.id,
    name: s.name,
    tagsInput: (s.query.tags?.any ?? []).join(', '),
    status: s.query.status ?? 'subscribed',
    sourcesInput: (s.query.sources ?? []).join(', '),
    minOpens: s.query.minOpens != null ? String(s.query.minOpens) : '',
  };
}

export function SegmentsPage() {
  const navigate = useNavigate();
  const [segments, setSegments] = useState<Segment[]>([]);
  const [editor, setEditor] = useState<EditorState>(EMPTY);
  const [preview, setPreview] = useState<SegmentPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memberPreviews, setMemberPreviews] = useState<Record<number, SegmentPreview | null>>({});
  const [openMembers, setOpenMembers] = useState<number | null>(null);

  useEffect(() => { refresh(); }, []);

  async function refresh() {
    try {
      const r = await api.listSegments();
      setSegments(r.segments);
      loadMemberCounts(r.segments);
    } catch (e) {
      setError(`Load failed: ${(e as Error).message}`);
    }
  }

  // Live counts expose dead segments (0 members) at a glance. Best-effort:
  // a failed preview shows as "?" rather than blocking the page.
  async function loadMemberCounts(segs: Segment[]) {
    const results = await Promise.all(
      segs.map(async (s) => {
        try {
          const r = await api.previewSegment(s.query);
          return [s.id, { count: r.count, sample: r.sample }] as const;
        } catch {
          return [s.id, null] as const;
        }
      }),
    );
    setMemberPreviews(Object.fromEntries(results));
  }

  // Live preview after each editor change. Debounce so quick typing doesn't
  // hammer the Sheet/D1 join.
  useEffect(() => {
    if (!editor.name && !editor.tagsInput && !editor.sourcesInput && !editor.minOpens) {
      setPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const q = editorToQuery(editor);
        const r = await api.previewSegment(q);
        setPreview({ count: r.count, sample: r.sample });
        setError(null);
      } catch (e) {
        setError(`Preview failed: ${(e as Error).message}`);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [editor]);

  async function onSave() {
    if (!editor.name.trim()) { setError('Name required'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.saveSegment({ id: editor.id, name: editor.name.trim(), query: editorToQuery(editor) });
      setEditor(EMPTY);
      setPreview(null);
      await refresh();
    } catch (e) {
      setError(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id: number, name: string) {
    if (!confirm(`Delete segment "${name}"?`)) return;
    try {
      await api.deleteSegment(id);
      await refresh();
    } catch (e) {
      setError(`Delete failed: ${(e as Error).message}`);
    }
  }

  return (
    <>
      {error && <div className="error">{error}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>
          {editor.id ? 'Edit segment' : 'Create segment'}
        </h3>

        <div className="field">
          <label>Name</label>
          <input
            type="text"
            value={editor.name}
            onChange={(e) => setEditor({ ...editor, name: e.target.value })}
            placeholder="e.g. Engaged beta users"
          />
        </div>

        <div className="field">
          <label>Tags (any of, comma-separated)</label>
          <input
            type="text"
            value={editor.tagsInput}
            onChange={(e) => setEditor({ ...editor, tagsInput: e.target.value })}
            placeholder="beta, vip, early-access"
          />
        </div>

        <div className="field">
          <label>Sources (any of, comma-separated)</label>
          <input
            type="text"
            value={editor.sourcesInput}
            onChange={(e) => setEditor({ ...editor, sourcesInput: e.target.value })}
            placeholder="checkout, footer-form"
          />
        </div>

        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Status</label>
            <select
              value={editor.status}
              onChange={(e) => setEditor({ ...editor, status: e.target.value as EditorState['status'] })}
              style={{
                background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
                borderRadius: 8, padding: '10px 12px', font: 'inherit', width: '100%',
              }}
            >
              <option value="subscribed">Subscribed</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Min opens (last 90 days)</label>
            <input
              type="number"
              min={0}
              value={editor.minOpens}
              onChange={(e) => setEditor({ ...editor, minOpens: e.target.value })}
              placeholder="0"
            />
          </div>
        </div>

        {preview && (
          <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
            Matches <strong>{preview.count}</strong> subscriber{preview.count === 1 ? '' : 's'}.
            {preview.sample.length > 0 && (
              <> First few: {preview.sample.map((s) => s.email).join(', ')}</>
            )}
          </div>
        )}

        <div className="row" style={{ gap: 6, marginTop: 12 }}>
          <button className="primary" onClick={onSave} disabled={busy}>
            {busy ? 'Saving…' : editor.id ? 'Update segment' : 'Save segment'}
          </button>
          {editor.id && (
            <button className="secondary" onClick={() => setEditor(EMPTY)} disabled={busy}>
              Cancel edit
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Saved segments</h3>
        {segments.length === 0 ? (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>No segments yet.</p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {segments.map((s) => {
              const mp = memberPreviews[s.id];
              const count = mp?.count;
              return (
              <li
                key={s.id}
                style={{
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <strong>{s.name}</strong>
                    {count != null ? (
                      <span
                        className={`badge ${count === 0 ? 'unsubscribed' : 'subscribed'}`}
                        style={{ marginLeft: 8, fontSize: 11 }}
                        title={count === 0 ? 'This segment matches nobody — check its tags/sources' : undefined}
                      >
                        {count} member{count === 1 ? '' : 's'}
                      </span>
                    ) : (
                      <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>counting…</span>
                    )}
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {summarizeQuery(s.query)}
                      {count != null && count > 0 && (
                        <>
                          {' · '}
                          <button
                            type="button"
                            className="link-btn"
                            style={{ padding: 0, fontSize: 12 }}
                            onClick={() => setOpenMembers(openMembers === s.id ? null : s.id)}
                          >
                            {openMembers === s.id ? 'Hide members' : 'Preview members'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 6 }}>
                    <button
                      className="primary"
                      onClick={() => navigate('/compose', { state: { segmentId: s.id } })}
                      disabled={count === 0}
                      title={count === 0 ? 'Segment matches 0 subscribers' : `Compose an email to "${s.name}"`}
                    >
                      Send
                    </button>
                    <button className="secondary" onClick={() => setEditor(queryToEditor(s))}>Edit</button>
                    <button
                      className="secondary"
                      style={{ color: 'var(--danger)' }}
                      onClick={() => onDelete(s.id, s.name)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {openMembers === s.id && mp && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }}>
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                      {mp.sample.map((m) => (
                        <Link key={m.email} to={`/subscriber/${encodeURIComponent(m.email)}`}>
                          {m.name ? `${m.name} · ${m.email}` : m.email}
                        </Link>
                      ))}
                    </span>
                    {mp.count > mp.sample.length && (
                      <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                        Showing first {mp.sample.length} of {mp.count}.
                      </div>
                    )}
                  </div>
                )}
              </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

function summarizeQuery(q: SegmentQuery): string {
  const parts: string[] = [];
  parts.push(`status: ${q.status ?? 'subscribed'}`);
  if (q.tags?.any?.length) parts.push(`tags: ${q.tags.any.join(' | ')}`);
  if (q.sources?.length) parts.push(`sources: ${q.sources.join(' | ')}`);
  if (q.minOpens) parts.push(`min ${q.minOpens} opens (90d)`);
  return parts.join(' · ');
}
